import type React from 'react';

import { useTranslation } from 'react-i18next';

import type { Company } from '../../../domain/company';
import { LeadPriorityScore } from '../../../domain/signal';
import { useSignalsQuery } from '../../../features/signals/useSignalsQuery';
import { useUserStatus } from '../../../hooks/useUserStatus';
import { SectionHeader } from '../SectionHeader';
import { StyledSectionBody, StyledSectionView } from '../styles';
import { useWidgetScrollIntoView } from '../useWidgetScrollIntoView';

import { EmptyState } from './EmptyState/EmptyState';
import { LeadScoringAgent } from './LeadScoringAgent/LeadScoringAgent';
import { LoadingState } from './LoadingState/LoadingState';
import { SignalsContent } from './SignalsContent/SignalsContent';

export const Signals: React.FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation();
  const ref = useWidgetScrollIntoView('signals');
  const { isTrialOrAfterTrial, isNoTouch } = useUserStatus();
  const isShowIntentBanner = isTrialOrAfterTrial || isNoTouch;

  const { data, isLoading, isError } = useSignalsQuery(company.domain);

  const signals = data?.items ?? [];
  const leadPriority = data?.leadPriority;

  const isEmpty = !signals?.length || isError;

  const renderContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (isEmpty && !isShowIntentBanner) {
      return <EmptyState />;
    }

    return <SignalsContent data={signals} company={company} />;
  };

  return (
    <StyledSectionView withPadding data-automation="CompanyOverviewSignals" ref={ref}>
      <SectionHeader title={t('company.signals.title')} withSectionPadding />
      <StyledSectionBody>
        {leadPriority !== undefined ? (
          <LeadScoringAgent
            score={leadPriority?.score ?? LeadPriorityScore.Unlock}
            domain={company.domain}
            signals={leadPriority?.triggeredSignals ?? []}
          />
        ) : null}
        {renderContent()}
      </StyledSectionBody>
    </StyledSectionView>
  );
};
